import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import AllowedOptionsService from "../../services/settings/AllowedOptionsService";
import { setCodes, resetCodes, setGrouped } from "../../store/slices/settings/allowedOptionsSlice";
import { AllowedOptionDTO, AllowedOptionListDTO } from "../../types/settings/allowedOptionsInterfaces";
import useApiErrorHandler from "../useApiErrorHandler";
import useModals from "../useModals";
import { Module, TargetType } from "../../types/common/enums";

/**
 * Hook para interactuar con las opciones permitidas (allowed options).
 */
export const useAllowedOptions = () => {
    const dispatch: AppDispatch = useDispatch();
    const { codes, grouped } = useSelector((state: RootState) => state.allowedOptions);
    const { handle } = useApiErrorHandler();
    const { openModal } = useModals();

    const fetchCodesByModuleAndTarget = async (module: Module, target: TargetType) => {
        try {
            const response = await AllowedOptionsService.fetchAllowedCodes(module, target);
            const { statusCode, data } = response;
            if (statusCode === 200) {
                dispatch(setCodes({ module, target, codes: data }));
            }
        } catch (error) {
            handle(error);
        }
    };

    const getCodes = (module: Module, target: TargetType): string[] => {
        const key = `${module}-${target}`;
        return codes[key] ?? [];
    };


    const fetchGroupedOptions = async () => {
        try {
            const response = await AllowedOptionsService.fetchGroupedOptions();
            // console.log(response)
            const { statusCode, type, title, message, data } = response;
            if (statusCode === 200) {
                dispatch(setGrouped(data));
                openModal(type, {
                    title,
                    message,
                });
            }
        } catch (error) {
            handle(error);
        }
    };

    const updateOptionList = async (payload: AllowedOptionListDTO) => {
        try {
            const response = await AllowedOptionsService.updateOptionList(payload);
            const { statusCode, type, title, message } = response;
            if (statusCode === 200) {
                openModal(type, {
                    title,
                    message,
                });
            }
        } catch (error) {
            handle(error);
        }
    };

    const updateSingleOption = async (option: AllowedOptionDTO) => {
        try {
            const response = await AllowedOptionsService.updateSingleOption(option);
            const { statusCode, type, title, message } = response;
            if (statusCode === 200) {
                openModal(type, {
                    title,
                    message,
                });
            }

        } catch (error) {
            handle(error);
        }
    };

    return {
        codes,
        grouped,
        fetchCodesByModuleAndTarget,
        getCodes,
        resetCodes,
        fetchGroupedOptions,
        updateOptionList,
        updateSingleOption,
    };
};
